import { Doc, Id } from "@cvx/_generated/dataModel";
import { formatNumber, truncateText } from "@/utils/format";

import { Link } from "@tanstack/react-router";
import PlatformIcon from "@/components/Generic/PlatformIcon";
import { format } from "date-fns";

type Video = Doc<"contractVideos"> & {
  views?: number;
  likes?: number;
  comments?: number;
  shares?: number;
  bookmarks?: number;
};

type CreatorWithName = Doc<"creators"> & {
  name: string;
};

interface VideosTableProps {
  videos: Video[];
  videoToContractMap: Record<string, string>;
  contractToCreatorMap: Record<string, string>;
  creatorMap: Map<string, CreatorWithName>;
}

export function VideosTable({
  videos,
  videoToContractMap,
  contractToCreatorMap,
  creatorMap,
}: VideosTableProps) {
  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="overflow-x-auto">
        {videos.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <p className="mb-4 text-lg text-primary/60">
              No approved videos found
            </p>
            <p className="text-sm text-primary/60">
              Approved videos from the selected campaigns will appear here.
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="border-b border-border bg-secondary">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                  Title
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                  Creator
                </th>
                <th className="w-16 px-6 py-3 text-center text-sm font-medium text-primary/60">
                  Platform
                </th>
                <th className="px-6 py-3 text-right text-sm font-medium text-primary/60">
                  Views
                </th>
                <th className="px-6 py-3 text-right text-sm font-medium text-primary/60">
                  Likes
                </th>
                <th className="px-6 py-3 text-right text-sm font-medium text-primary/60">
                  Bookmarks
                </th>
                <th className="px-6 py-3 text-right text-sm font-medium text-primary/60">
                  Shares
                </th>
                <th className="px-6 py-3 text-right text-sm font-medium text-primary/60">
                  Comments
                </th>
                <th className="px-6 py-3 text-right text-sm font-medium text-primary/60">
                  Date Posted
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {videos.map((video) => {
                const contractId = videoToContractMap[video._id];
                const creatorId = contractId
                  ? contractToCreatorMap[contractId]
                  : null;
                const creator = creatorId
                  ? creatorMap.get(creatorId as Id<"creators">)
                  : null;

                return (
                  <tr key={video._id} className="hover:bg-secondary/50">
                    <td className="px-6 py-4">
                      <a
                        href={video.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline"
                      >
                        {truncateText(video.title ?? "No title", 60)}
                      </a>
                    </td>
                    <td className="px-6 py-4">
                      {creator ? (
                        <Link
                          to="/creators/$creatorId"
                          params={{ creatorId: creator._id }}
                          className="text-sm text-primary hover:underline"
                        >
                          {creator.name}
                        </Link>
                      ) : (
                        <span className="text-sm text-primary">
                          Unknown Creator
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-center">
                        <PlatformIcon platform={video.platform ?? ""} />
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-primary">
                      {formatNumber(video.views ?? 0)}
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-primary">
                      {formatNumber(video.likes ?? 0)}
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-primary">
                      {video.platform === "youtube" ||
                      video.platform === "youtube-shorts" ||
                      video.platform === "tiktok"
                        ? "N/A"
                        : formatNumber(video.bookmarks ?? 0)}
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-primary">
                      {video.platform === "youtube" ||
                      video.platform === "youtube-shorts"
                        ? "N/A"
                        : formatNumber(video.shares ?? 0)}
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-primary">
                      {formatNumber(video.comments ?? 0)}
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-primary">
                      {video.postDate
                        ? format(new Date(video.postDate), "dd/MM/yy")
                        : "-"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
